/**
 * Caja para conectar la cuenta con el código de acceso.
 *
 * Se muestra cuando la app INSTALADA arranca sin ancla (en iOS el sandbox de la
 * app no comparte storage con Safari, y arranca en "/" sin el código de la URL).
 * El promotor tiene su código en el mensaje de WhatsApp; acá lo escribe o pega.
 *
 * Diseño robusto contra los edge cases de PEGAR (memoria: edges-pwa-instalacion-
 * clipboard):
 *   - CAMINO PRIMARIO: un input visible de 6 dígitos (inputmode=numeric,
 *     font-size≥16px para que iOS no haga auto-zoom). El promotor escribe o pega
 *     a mano con el teclado — esto NO pide permiso de clipboard, funciona siempre.
 *   - AYUDA OPCIONAL: botón "Pegar" (navigator.clipboard.readText) donde el
 *     navegador lo permita; si falla o pide permiso, no rompe nada.
 *   - El evento 'paste' lee SÍNCRONO de clipboardData (diferirlo da string vacío).
 *   - Sanitiza todo con extraerTokenAcceso (acepta URL completa, mensaje entero o
 *     código pelado) → "que entre porque entre".
 *   - Guard anti-doble-ejecución al conectar.
 */

import { t } from '../languages.js';
import ApiClient from '../api-client.js';
import { extraerTokenAcceso, CODIGO_CORTO_REGEX, ACCESO_HEX64_REGEX } from '@shared/config/acceso-corto.js';

/**
 * @param {{ onConectado: () => void }} cb - onConectado se llama cuando el canje
 *        deja un dispositivoToken usable → el orquestador sigue el flujo normal.
 * @returns {HTMLElement}
 */
export function renderCajaCodigo({ onConectado }) {
    const el = document.createElement('div');
    el.className = 'pm-codigo';
    el.innerHTML = `
        <div class="pm-codigo-emoji">🔑</div>
        <h1 class="pm-codigo-titulo">${t('bienvenida.codigoTitulo')}</h1>
        <p class="pm-codigo-texto">${t('bienvenida.codigoTexto')}</p>
        <input class="pm-codigo-input" id="pm-codigo-input" type="text"
            inputmode="numeric" autocomplete="one-time-code" maxlength="6"
            placeholder="••••••" aria-label="${t('bienvenida.codigoPlaceholder')}" />
        <div class="pm-codigo-acciones">
            <button class="pm-codigo-pegar" id="pm-codigo-pegar" type="button">${t('bienvenida.codigoPegar')}</button>
            <button class="pm-codigo-conectar" id="pm-codigo-conectar" type="button">${t('bienvenida.codigoConectar')}</button>
        </div>
        <div class="pm-codigo-error" id="pm-codigo-error" role="alert"></div>`;

    const input = el.querySelector('#pm-codigo-input');
    const btnPegar = el.querySelector('#pm-codigo-pegar');
    const btnConectar = el.querySelector('#pm-codigo-conectar');
    const errorEl = el.querySelector('#pm-codigo-error');

    let conectando = false;

    function mostrarError(msg) {
        errorEl.textContent = msg || '';
    }

    // Extrae el token de un texto pegado (URL, mensaje entero o pegado sucio).
    // Devuelve el valor crudo: 6 dígitos (corto) o 64 hex (legacy). No trunca —
    // un promotor con link hex64 viejo también tiene que poder conectar acá.
    function aCodigo(textoCrudo) {
        const extraido = extraerTokenAcceso(textoCrudo);
        return extraido ? extraido.valor : '';
    }

    async function conectar() {
        if (conectando) return; // guard anti-doble-tap
        const crudo = input.value.trim();
        const extraido = extraerTokenAcceso(crudo);
        if (!extraido) {
            mostrarError(t('bienvenida.codigoError'));
            input.focus();
            return;
        }
        conectando = true;
        btnConectar.disabled = true;
        btnPegar.disabled = true;
        const textoOriginal = btnConectar.textContent;
        btnConectar.textContent = t('bienvenida.codigoConectando');
        mostrarError('');
        try {
            const ok = await ApiClient.canjearTokenAcceso(extraido.valor);
            if (ok) {
                if (onConectado) onConectado();
                return; // no re-habilitar: la vista se reemplaza
            }
            // El backend rechazó (inexistente/usado/vencido) o no hubo red.
            mostrarError(navigator.onLine ? t('bienvenida.codigoError') : t('bienvenida.codigoErrorRed'));
        } catch (_) {
            mostrarError(t('bienvenida.codigoErrorRed'));
        } finally {
            conectando = false;
            btnConectar.disabled = false;
            btnPegar.disabled = false;
            btnConectar.textContent = textoOriginal;
        }
    }

    // Coloca el código extraído en el input y autoconecta si está completo. El
    // caso normal es 6 dígitos; un hex64 legacy (64 chars) también se acepta y
    // autoconecta. El maxlength del input (6) NO aplica a value seteado por JS.
    function ponerCodigoYConectar(cod) {
        if (!cod) return;
        input.value = cod;
        mostrarError('');
        if (CODIGO_CORTO_REGEX.test(cod) || ACCESO_HEX64_REGEX.test(cod)) conectar();
    }

    // El evento 'paste' se maneja SÍNCRONO — leemos clipboardData en el handler,
    // no diferido (diferirlo o leer .value en el mismo tick da string vacío). El
    // promotor puede pegar el MENSAJE ENTERO de WhatsApp; extraerTokenAcceso saca el
    // código de adentro. preventDefault SIEMPRE para que el handler 'input' no
    // trunque el texto pegado antes de extraer.
    input.addEventListener('paste', e => {
        const pegado = (e.clipboardData || window.clipboardData)?.getData('text') || '';
        e.preventDefault();
        const cod = aCodigo(pegado);
        if (cod) {
            ponerCodigoYConectar(cod);
        } else {
            // Pegó algo sin código reconocible → mostrar los dígitos que haya, sin romper.
            const soloDigitos = pegado.replace(/\D/g, '').slice(0, 6);
            input.value = soloDigitos;
            mostrarError(soloDigitos.length === 6 ? '' : t('bienvenida.codigoError'));
        }
    });
    input.addEventListener('input', () => {
        // Tipeo manual: solo dígitos, máx 6 (el caso normal del promotor nuevo).
        // El hex64 legacy NO se teclea a mano — llega por pegar (ver paste/botón).
        const limpio = input.value.replace(/\D/g, '').slice(0, 6);
        if (limpio !== input.value) input.value = limpio;
        mostrarError('');
    });

    // Botón "Pegar": intenta readText (Clipboard API). En iOS suele pedir permiso o
    // fallar — por eso, si no logra leer, NO se queda mudo: enfoca el input e indica
    // pegar a mano (el handler 'paste' de arriba extrae el código del mensaje igual).
    btnPegar.addEventListener('click', async () => {
        let texto = '';
        try {
            if (navigator.clipboard && navigator.clipboard.readText) {
                texto = await navigator.clipboard.readText();
            }
        } catch (_) {
            texto = ''; // iOS negó el permiso o no soporta readText
        }
        const cod = texto ? aCodigo(texto) : '';
        if (cod) {
            ponerCodigoYConectar(cod);
            return;
        }
        // No se pudo leer el portapapeles (iOS típico): guiar al pegado manual.
        input.focus();
        mostrarError(t('bienvenida.codigoPegarManual'));
    });

    btnConectar.addEventListener('click', conectar);

    // Foco inicial para que el teclado numérico aparezca de una.
    setTimeout(() => input.focus(), 60);

    return el;
}
